import { toRegionName } from './regionCodeMaps';

const getAddressComponent = (components, type) =>
	components?.find((component) => component.types.includes(type));

// Converts a Google Place's address_components into our address shape.
// Returns { country: '' } when the place is outside the US, CA, or AU.
export const parseGoogleAddress = (components = []) => {
	const country = getAddressComponent(components, 'country')?.short_name || '';
	const streetNumber =
		getAddressComponent(components, 'street_number')?.long_name || '';
	const route = getAddressComponent(components, 'route')?.long_name || '';
	const street = [streetNumber, route].filter(Boolean).join(' ').trim();
	const city =
		getAddressComponent(components, 'locality')?.long_name ||
		getAddressComponent(components, 'postal_town')?.long_name ||
		getAddressComponent(components, 'administrative_area_level_2')?.long_name ||
		'';
	const suburb =
		getAddressComponent(components, 'sublocality_level_1')?.long_name ||
		getAddressComponent(components, 'sublocality')?.long_name ||
		city;
	const adminAreaComponent = getAddressComponent(
		components,
		'administrative_area_level_1',
	);
	const adminArea =
		adminAreaComponent?.short_name || adminAreaComponent?.long_name || '';
	const postalCode =
		getAddressComponent(components, 'postal_code')?.long_name || '';

	if (country === 'US') {
		return {
			street,
			city,
			// Prefer full name for UI select; callers normalize to code on submit.
			state: toRegionName({ country: 'US', codeOrName: adminArea }),
			zipCode: postalCode,
			country,
		};
	}

	if (country === 'CA') {
		return {
			street,
			city,
			province: toRegionName({ country: 'CA', codeOrName: adminArea }),
			postalCode,
			country,
		};
	}

	if (country === 'AU') {
		return {
			street,
			suburb,
			state: toRegionName({ country: 'AU', codeOrName: adminArea }),
			postalCode,
			country,
		};
	}

	return { country: '' };
};

export const getPlaceDetails = (placeId, fields = ['address_components']) =>
	new Promise((resolve, reject) => {
		if (!window.google?.maps?.places) {
			reject(new Error('Google Places is not available.'));
			return;
		}

		const service = new window.google.maps.places.PlacesService(
			document.createElement('div'),
		);

		service.getDetails({ placeId, fields }, (place, status) => {
			if (
				status !== window.google.maps.places.PlacesServiceStatus.OK ||
				!place
			) {
				reject(new Error(status));
				return;
			}
			resolve(place);
		});
	});

// Google often returns a GMB tracking URL
// (utm_source=google&utm_medium=organic&utm_campaign=GMB). Keep origin + path.
export const stripWebsiteTracking = (website) => {
	if (!website || typeof website !== 'string') return '';
	try {
		const url = new URL(website);
		const path = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
		return `${url.origin}${path}`;
	} catch {
		return website.split(/[?#]/)[0].replace(/\/$/, '');
	}
};
